import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router';

import { createApiClient } from '@/api/client';
import type { ApiError, LegacyKuduResponseEnvelope } from '@/api/client.types';
import { createLegacyKuduToken } from '@/api/legacyKuduAuth';
import { useAuth } from '@/auth/useAuth';
import type { RawComponent } from '@/features/content/useComponent';
import { courseQueryKey } from '@/features/courses/useCourse';

import type { EditableCoach } from './kaiCoachEditorModel';
import { buildCoachUpdatePayload } from './kaiCoachSavePayload';

export interface SaveKaiCoachContext {
  // The component as loaded; cloned on save so visibility/timing/other fields
  // persist across coach edits.
  baseComponent: RawComponent;
  coachUid: string;
  componentId: string;
  courseId: string;
}

interface UpdateCoursePayload {
  components: Array<Record<string, unknown>>;
  id: string;
}

function createDataError(message: string): ApiError {
  const error = new Error(message) as ApiError;
  error.kind = 'data';
  return error;
}

/**
 * Persists a Kai Coach module through `UpdateCourse`. The backend matches the
 * component by uid and replaces it wholesale, so we send a clone of the loaded
 * component with only its `kai_coach_section` swapped — preserving visibility
 * (`is_live`), the content/homework start timestamps, and every other field
 * across edits.
 */
export function useSaveKaiCoach(context: SaveKaiCoachContext) {
  const { getIdToken, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, EditableCoach>({
    mutationFn: async (coach) => {
      if (!user) {
        throw createDataError('You must be signed in to save this coach.');
      }
      const returnTo = `${location.pathname}${location.search}${location.hash}`;
      const client = createApiClient({
        baseUrl: import.meta.env.VITE_API_BASE_URL || '',
        getIdToken,
        getLegacyToken: async () => createLegacyKuduToken(user),
        onAuthError: () =>
          navigate(`/login?returnTo=${encodeURIComponent(returnTo)}`, {
            replace: true,
          }),
      });

      const payload: UpdateCoursePayload = buildCoachUpdatePayload(
        context.courseId,
        context.baseComponent,
        coach,
        context.coachUid,
      );

      await client.invokeKuduMethod<
        unknown | LegacyKuduResponseEnvelope<unknown>,
        UpdateCoursePayload
      >('UpdateCourse', payload);

      await queryClient.invalidateQueries({
        queryKey: courseQueryKey(context.courseId),
      });
      await queryClient.invalidateQueries({
        exact: false,
        queryKey: ['component', context.courseId, context.componentId],
      });
    },
  });
}
